import React, { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SxProps, Theme } from '@mui/system';
import InputLabel from '@mui/material/InputLabel';

import CheckboxCustom from './CheckboxCustom';

import { inputBorderLight, dividerBorderLight } from 'src/common/styles';
import { ItemType } from 'src/components/common/field/TypeCustom';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 16;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 3 + ITEM_PADDING_TOP,
      px: '20px',
      py: '16px',
      backgroundColor: 'primary.light',
      ...dividerBorderLight,
      borderRadius: '4px'
    },
  },
};

type DropDownMultiSelectProps = {
  selected: number[];
  placeholder?: string;
  idSelect: string;
  items?: ItemType[];
  parentId?: number | null;
  sx?: SxProps<Theme>;
  sxLabel?: SxProps<Theme>;
  sxInput?: SxProps<Theme>;
  onChange: (itemIds: number[]) => void;
  evenClose?: () => void;
  evenOpen?: () => void;
};

const DropDownMultiSelect: React.FC<DropDownMultiSelectProps> = (props) => {

  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    const itemNames = props.items ? props.items.filter(item => props.selected.includes(Number(item.id))).map(item => item.name) : [];

    itemNames.length !== 0 ? setShowPlaceholder(false) : setShowPlaceholder(true);
    setSelected(itemNames);
    // eslint-disable-next-line
  }, [props.selected]);

  const handleChange = (event: SelectChangeEvent<typeof selected>) => {
    const {
      target: { value },
    } = event;

    const listName = typeof value === 'string' ? value.split(',') : value;

    const itemIds = props.items ? props.items.filter(item => listName && listName.includes(item.name)).map(item => Number(item.id)) : [];

    props.onChange(itemIds);
  };

  const handleClose = () => {
    props.evenClose?.();
  };

  const handleOpen = () => {
    props.evenOpen?.();
  };

  return (
    <Box>
      <FormControl fullWidth sx={{ ...props.sx }}>
        {showPlaceholder && (props.placeholder && props.placeholder.length !== 0) && (
          <InputLabel
            id='multi-select-placeholder'
            sx={{
              color: '#AAB2BD',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '14.4px',
              '&.Mui-focused': { color: '#656D78', transform: 'translate(14px, 16px) scale(1)' },
              ...props.sxLabel
            }}
          >
            {props.placeholder}
          </InputLabel>
        )}
        <Select
          labelId='multi-select-placeholder'
          id={props.idSelect}
          multiple
          value={selected}
          onChange={handleChange}
          onClose={handleClose}
          onOpen={handleOpen}
          renderValue={(selected) => selected.join(', ')}
          MenuProps={MenuProps}
          sx={{
            height: '48px',
            ...inputBorderLight,
            ...props.sxInput
          }}
        >
          {props.items && props.items.map((value, index) => (
            (props.parentId && String(props.parentId) === String(value.parentId) && (
              <MenuItem key={index} value={value.name} sx={{
                height: '40px',
                borderBottom: props.items && (index === props.items.length - 1) ? 'none' : '1px solid',
                ...dividerBorderLight
              }}>
                <CheckboxCustom
                  label={value.name}
                  sx={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    overflow: 'hidden'
                  }}
                  checked={selected.indexOf(value.name) > -1} />
              </MenuItem>
            ))
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default DropDownMultiSelect;